import { z } from "zod";

/**
 * GiftPass scan — Flows §1/§3: each scan validates + redeems atomically
 * (bearer instrument, not a wallet). BNB-XXXX-XXXX is the human index; the
 * UUID stays server-side (SECURITY §3).
 */
/**
 * A GiftPass code as stored and printed: BNB-XXXX-XXXX from the system, or a
 * printed card's 17-character number (MMDD + running number + batch + year +
 * design letter, e.g. 0824000100082026D) imported by 0376.
 */
export const GIFTPASS_CODE = /^(BNB-[A-Z0-9]{4}-[A-Z0-9]{4}|[0-9]{12}20[0-9]{2}[A-Z])$/;

export const redeemGiftpassSchema = z.object({
  displayCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(GIFTPASS_CODE, "Enter the code on the card, like BNB-XXXX-XXXX."),
});

export type RedeemGiftpassInput = z.infer<typeof redeemGiftpassSchema>;

/**
 * Admin reactivate — 0018's own comment: "admin reactivates with one click;
 * audit_log written by that flow." Scoped server-side to passes still in
 * 'revalidation_required' (never consumed) — see reactivate_giftpass (0022).
 */
export const reactivateGiftpassSchema = z.object({
  reason: z.string().min(3).max(500),
});

export type ReactivateGiftpassInput = z.infer<typeof reactivateGiftpassSchema>;

/**
 * Admin: generate a batch (0223). The label is company and/or campaign — 0008
 * has no name column — and at least one is required. The count bound mirrors
 * `create_giftpass_batch`; it is a technical ceiling per click, not a policy.
 * No expiry (GFT-002) and no revalidation date: see 0223's header.
 */
export const GIFTPASS_BATCH_MAX = 1000;

export const createGiftpassBatchSchema = z
  .object({
    company: z.string().trim().max(120).default(""),
    campaign: z.string().trim().max(120).default(""),
    count: z.number().int().min(1).max(GIFTPASS_BATCH_MAX),
  })
  .refine((v) => v.company !== "" || v.campaign !== "", {
    message: "Enter a company or a campaign.",
    path: ["company"],
  });

export type CreateGiftpassBatchInput = z.input<typeof createGiftpassBatchSchema>;

/** Superadmin: import printed cards by their card numbers (0376, G9). At most 1,000 a call. */
export const importPrintedGiftpassesSchema = z.object({
  label: z.string().trim().min(1, "Name this set of cards.").max(120),
  codes: z
    .array(
      z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[0-9]{12}20[0-9]{2}[A-Z]$/, "Not a card number."),
    )
    .min(1, "Paste at least one card number.")
    .max(GIFTPASS_BATCH_MAX, "Import at most 1,000 at a time."),
});
export type ImportPrintedGiftpassesInput = z.input<typeof importPrintedGiftpassesSchema>;

/**
 * One row of the signed-in client's own GiftPass history —
 * `v_client_giftpass_history` (migration 0032, PAY-02).
 *
 * Read-only and owner-scoped: the view's `auth.uid()` predicate is the row
 * scope, so this shape carries no owner field to check on the client.
 *
 * BEARER SEMANTICS (OQ-20): a pass has no `owner_id` and no stored peso
 * balance, so there is deliberately no `balance` here and none may be derived
 * as if the model had one. `amount` is the ₱299 a single consumed pass
 * contributed to one booking — it is a ledger fact, not a wallet.
 *
 * `appliedAt`/`amount`/`bookingRef` are null while a pass has been scanned but
 * not yet linked to a booking by `link_giftpasses` (0018).
 */
export const clientGiftpassHistoryEntrySchema = z
  .object({
    displayCode: z.string().regex(GIFTPASS_CODE),
    /** 0272: the history EVENT — used on a booking, or returned by its cancellation. */
    status: z.enum(["redeemed", "restored"]),
    redeemedAt: z.string().datetime({ offset: true }).nullable(),
    appliedAt: z.string().datetime({ offset: true }).nullable(),
    amount: z.number().nullable(),
    bookingRef: z.string().nullable(),
  })
  .strict();

export type ClientGiftpassHistoryEntry = z.infer<typeof clientGiftpassHistoryEntrySchema>;
